import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface Distribution {
  id: string;
  lead_id: string;
  status: string;
  company_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  lead_email?: string;
  lead_phone?: string;
  state?: string;
  zip?: string;
  source?: string;
  territory_name?: string;
  suggested_dealer_id?: string;
  suggested_dealer_name?: string;
  suggested_sales_rep_id?: string;
  suggested_rep_first?: string;
  suggested_rep_last?: string;
  assigned_target_type?: string;
  assigned_dealer_name?: string;
  assigned_rep_first?: string;
  assigned_rep_last?: string;
}

interface Targets {
  dealers: { id: string; company_name: string; address_state?: string }[];
  reps: { id: string; first_name?: string; last_name?: string }[];
}

const STATUSES = ['pending', 'approved', 'rejected'] as const;

export default function LeadDistributionPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('pending');
  const [rows, setRows] = useState<Distribution[]>([]);
  const [targets, setTargets] = useState<Targets>({ dealers: [], reps: [] });
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Per-row override selections
  const [pickDealer, setPickDealer] = useState<Record<string, string>>({});
  const [pickRep, setPickRep] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, tgt] = await Promise.all([
        api.get<Distribution[]>(`/lead-distributions?status=${status}`),
        api.get<Targets>('/lead-distributions/targets').catch(() => ({ dealers: [], reps: [] })),
      ]);
      setRows(list || []);
      setTargets(tgt || { dealers: [], reps: [] });
    } catch (e: any) {
      setNotice(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (d: Distribution, targetType: 'dealer' | 'rep') => {
    setBusy(d.id);
    setNotice(null);
    try {
      const body: any = { targetType };
      if (targetType === 'dealer') body.dealerId = pickDealer[d.id] || d.suggested_dealer_id;
      else body.salesRepId = pickRep[d.id] || d.suggested_sales_rep_id;
      if (targetType === 'dealer' && !body.dealerId) {
        setNotice('Pick a dealer first.');
        setBusy(null);
        return;
      }
      if (targetType === 'rep' && !body.salesRepId) {
        setNotice('Pick a sales rep first.');
        setBusy(null);
        return;
      }
      await api.post(`/lead-distributions/${d.id}/approve`, body);
      setNotice('Lead assigned and notified.');
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Approve failed');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (d: Distribution) => {
    const reason = prompt('Reason for rejecting this distribution? (optional)') ?? undefined;
    setBusy(d.id);
    setNotice(null);
    try {
      await api.post(`/lead-distributions/${d.id}/reject`, { reason });
      setNotice('Distribution rejected.');
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Reject failed');
    } finally {
      setBusy(null);
    }
  };

  const leadName = (d: Distribution) =>
    d.company_name ||
    [d.contact_first_name, d.contact_last_name].filter(Boolean).join(' ') ||
    d.lead_email ||
    'Lead';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <PageHeader
        title="Lead Distribution"
        subtitle="System recommends a dealer/rep by territory. Nothing is assigned until you approve."
      />

      <div className="flex gap-1 border-b">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              status === s ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {notice && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 text-sm">{notice}</div>
      )}

      {loading ? (
        <div className="py-10 flex justify-center"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <Card><p className="text-sm text-gray-500 py-6 text-center">No {status} distributions.</p></Card>
      ) : (
        <div className="space-y-4">
          {rows.map((d) => (
            <Card key={d.id}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{leadName(d)}</p>
                  <p className="text-sm text-gray-500">
                    {[d.lead_email, d.lead_phone, [d.state, d.zip].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Territory: {d.territory_name || '—'}
                    {d.source ? ` · Source: ${d.source}` : ''}
                  </p>
                </div>

                {status === 'pending' ? (
                  <div className="flex flex-col gap-3 md:w-[430px]">
                    {/* Assign to dealer */}
                    <div className="flex items-center gap-2">
                      <select
                        className="input flex-1"
                        value={pickDealer[d.id] ?? d.suggested_dealer_id ?? ''}
                        onChange={(e) => setPickDealer({ ...pickDealer, [d.id]: e.target.value })}
                      >
                        <option value="">Select dealer…</option>
                        {targets.dealers.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.company_name}{x.address_state ? ` (${x.address_state})` : ''}
                            {x.id === d.suggested_dealer_id ? ' — recommended' : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={busy === d.id}
                        onClick={() => approve(d, 'dealer')}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                      >
                        → Dealer
                      </button>
                    </div>
                    {/* Assign to rep */}
                    <div className="flex items-center gap-2">
                      <select
                        className="input flex-1"
                        value={pickRep[d.id] ?? d.suggested_sales_rep_id ?? ''}
                        onChange={(e) => setPickRep({ ...pickRep, [d.id]: e.target.value })}
                      >
                        <option value="">Select sales rep…</option>
                        {targets.reps.map((x) => (
                          <option key={x.id} value={x.id}>
                            {[x.first_name, x.last_name].filter(Boolean).join(' ')}
                            {x.id === d.suggested_sales_rep_id ? ' — recommended' : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={busy === d.id}
                        onClick={() => approve(d, 'rep')}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                      >
                        → Rep
                      </button>
                    </div>
                    <button
                      disabled={busy === d.id}
                      onClick={() => reject(d)}
                      className="text-xs text-red-600 hover:underline self-end"
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 md:text-right">
                    {d.assigned_target_type === 'dealer'
                      ? `Assigned to dealer: ${d.assigned_dealer_name || '—'}`
                      : d.assigned_target_type === 'rep'
                        ? `Assigned to rep: ${[d.assigned_rep_first, d.assigned_rep_last].filter(Boolean).join(' ') || '—'}`
                        : status === 'rejected'
                          ? 'Rejected'
                          : '—'}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
